<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import type { BaseKpiPassThrough } from "@core/components/base/kpi/baseKpiPassThrough"

defineOptions({
    name: "OptimDashboardKpiSection",
})

// ================================
// TYPES & INTERFACES
// ================================
export type OptimDashboardKpiItem = {
    /** Libellé court (sous-titre KPI). */
    label: string
    /** Valeur affichée (déjà formatée). */
    value: string | number
    /** Clé stable pour BaseKPI / tracking. */
    type: string
    /** Texte d’aide (tooltip / title). */
    hint?: string
}

// ================================
// PROPS & EMITS
// ================================
withDefaults(
    defineProps<{
        /** Chargement initial des données. */
        loading: boolean
        titleTotal: string
        titleMean: string
        titleOther: string
        totalKpis: OptimDashboardKpiItem[]
        meanKpis: OptimDashboardKpiItem[]
        otherKpis: OptimDashboardKpiItem[]
        /** Sous-titre sous la valeur pour les blocs économie (ex. HTVA). */
        kpiHtvaCaption?: string
    }>(),
    {
        loading: false,
        kpiHtvaCaption: undefined,
    }
)

// ================================
// STATE
// ================================
/**
 * Pass-through de la carte KPI « total » (pleine largeur sur md+).
 */
const DASHBOARD_KPI_PT_TOTAL: BaseKpiPassThrough = {
    root: "md:col-span-3 w-full min-h-[8.5rem] max-w-none mx-0 py-10 px-6 md:px-10 bg-primary-50 border border-primary-200 rounded-md justify-center gap-3",
    title: "text-sm md:text-base font-medium text-primary-800 normal-case text-center max-w-3xl leading-snug",
    valueRow: "w-full flex-wrap justify-center items-baseline gap-x-1 gap-y-0",
    value: "text-2xl sm:text-3xl md:text-4xl lg:text-[2.5rem] font-semibold text-primary-950 tabular-nums tracking-tight",
    subtitle: "text-xs text-primary-600",
}

/**
 * Pass-through des cartes KPI en grille 3 (cellules sous-totaux / moyennes).
 */
const DASHBOARD_KPI_PT_CELL: BaseKpiPassThrough = {
    root: "w-full min-h-[8.5rem] min-w-0 max-w-none mx-0 py-8 px-4 md:px-6 bg-primary-50 border border-primary-200 rounded-md justify-center gap-2",
    title: "text-xs sm:text-sm font-medium text-primary-800 normal-case text-center leading-snug px-1",
    valueRow: "w-full justify-center items-baseline",
    value: "text-xl sm:text-2xl md:text-3xl font-semibold text-primary-950 tabular-nums",
    subtitle: "text-xs text-primary-600",
}

/**
 * Pass-through des cartes KPI « autres » (fond blanc, hauteur compacte).
 */
const DASHBOARD_KPI_PT_OTHER: BaseKpiPassThrough = {
    root: "w-full min-h-[6.5rem] min-w-0 max-w-none mx-0 py-6 px-3 md:px-5 bg-white border border-surface-200 rounded-md justify-center gap-2",
    title: "text-xs sm:text-sm font-medium text-primary-700 normal-case text-center leading-snug px-1",
    valueRow: "w-full justify-center items-center",
    value: "text-lg sm:text-xl md:text-2xl font-semibold text-primary-900 tabular-nums",
}

// ================================
// BUSINESS LOGIC
// ================================
/**
 * PassThrough du premier KPI d’une série (ligne pleine largeur sur md+).
 * @returns Configuration PrimeVue `pt` pour BaseKPI.
 */
function buildKpiPtTotal(): BaseKpiPassThrough {
    return DASHBOARD_KPI_PT_TOTAL
}

/**
 * PassThrough des KPI cellules grille 3 (total hors première ligne, moyennes).
 * @returns Configuration PrimeVue `pt` pour BaseKPI.
 */
function buildKpiPtCell(): BaseKpiPassThrough {
    return DASHBOARD_KPI_PT_CELL
}

/**
 * PassThrough des KPI « autres » (cartes blanches, hauteur réduite).
 * @returns Configuration PrimeVue `pt` pour BaseKPI.
 */
function buildKpiPtOther(): BaseKpiPassThrough {
    return DASHBOARD_KPI_PT_OTHER
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL — sections KPI dashboard -->
    <!-- ================================ -->
    <div
        class="optim-dashboard-kpi-section mx-auto flex w-full flex-col gap-20"
    >
        <section class="flex w-full flex-col gap-4">
            <h3 class="text-lg font-semibold text-surface-800">
                {{ titleTotal }}
            </h3>
            <div
                class="grid w-full grid-cols-1 gap-4 md:grid-cols-3"
                role="list"
                :aria-busy="loading"
            >
                <template v-if="loading">
                    <div class="md:col-span-3 w-full">
                        <BaseSkeleton
                            height="8.5rem"
                            class="w-full rounded-md"
                        />
                    </div>
                    <BaseSkeleton
                        v-for="i in 3"
                        :key="`sk-t-${i}`"
                        height="8.5rem"
                        class="w-full rounded-md"
                    />
                </template>
                <template v-else>
                    <BaseKPI
                        v-for="(kpi, index) in totalKpis"
                        :key="kpi.type"
                        :class="index === 0 ? 'md:col-span-3' : ''"
                        :label="kpi.label"
                        :value="kpi.value"
                        :type="kpi.type"
                        :title="kpi.hint"
                        :clickable="false"
                        :pt="index === 0 ? buildKpiPtTotal() : buildKpiPtCell()"
                    >
                        <template v-if="kpiHtvaCaption" #subtitle>
                            {{ kpiHtvaCaption }}
                        </template>
                    </BaseKPI>
                </template>
            </div>
        </section>

        <section class="flex w-full flex-col gap-4">
            <h3 class="text-lg font-semibold text-surface-800">
                {{ titleMean }}
            </h3>
            <div
                class="grid w-full grid-cols-1 gap-4 md:grid-cols-3"
                role="list"
            >
                <template v-if="loading">
                    <BaseSkeleton
                        v-for="i in 3"
                        :key="`sk-m-${i}`"
                        height="8.5rem"
                        class="w-full rounded-md"
                    />
                </template>
                <template v-else>
                    <BaseKPI
                        v-for="kpi in meanKpis"
                        :key="kpi.type"
                        :label="kpi.label"
                        :value="kpi.value"
                        :type="kpi.type"
                        :title="kpi.hint"
                        :clickable="false"
                        :pt="buildKpiPtCell()"
                    >
                        <template v-if="kpiHtvaCaption" #subtitle>
                            {{ kpiHtvaCaption }}
                        </template>
                    </BaseKPI>
                </template>
            </div>
        </section>

        <section class="flex w-full flex-col gap-4">
            <h3 class="text-lg font-semibold text-surface-800">
                {{ titleOther }}
            </h3>
            <div
                class="grid w-full grid-cols-1 gap-4 md:grid-cols-3"
                role="list"
            >
                <template v-if="loading">
                    <BaseSkeleton
                        v-for="i in 3"
                        :key="`sk-o-${i}`"
                        height="6.5rem"
                        class="w-full rounded-md"
                    />
                </template>
                <template v-else>
                    <BaseKPI
                        v-for="kpi in otherKpis"
                        :key="kpi.type"
                        :label="kpi.label"
                        :value="kpi.value"
                        :type="kpi.type"
                        :title="kpi.hint"
                        :clickable="false"
                        :pt="buildKpiPtOther()"
                    />
                </template>
            </div>
        </section>
    </div>
</template>
